/**
 * Lookup slice - reference data (states, countries, equipment, attributes, etc.)
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { toast } from 'react-toastify';
import { LOOKUP } from '../../api/endpoints';

export const getCountries = createAsyncThunk(
  'lookup/getCountries',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/countries');
      return data?.data?.list ?? data?.list ?? data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load countries');
      return rejectWithValue(err.response?.data?.message || 'Failed to load countries');
    }
  }
);

export const getUnitedStatesId = createAsyncThunk(
  'lookup/getUnitedStatesId',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/countries');
      const countries = data?.data?.list ?? data?.list ?? data ?? [];
      const us = Array.isArray(countries)
        ? countries.find(
            (c) =>
              c.name === 'United States' || c.name === 'United States of America'
          )
        : null;
      return us?.id ?? null;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load countries');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getStates = createAsyncThunk(
  'lookup/getStates',
  async (countryId, { rejectWithValue }) => {
    if (!countryId) return rejectWithValue('No country ID provided');
    try {
      const { data } = await api.get(LOOKUP.COUNTRY_BY_ID(countryId));
      return data?.data?.states ?? data?.states ?? data ?? [];
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load states');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getAttributeTypes = createAsyncThunk(
  'lookup/getAttributeTypes',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/attribute-types', { params });
      const list = data?.data?.list ?? data?.list ?? data ?? [];
      if (list?.length && typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem('attributeTypes', JSON.stringify(list));
        } catch (e) {
          console.warn('Could not store attributeTypes in localStorage', e);
        }
      }
      return list;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load attribute types');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getEquipmentTypes = createAsyncThunk(
  'lookup/getEquipmentTypes',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LOOKUP.EQUIPMENT_TYPES);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load equipment types');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getEquipmentModels = createAsyncThunk(
  'lookup/getEquipmentModels',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LOOKUP.EQUIPMENT_MODELS, { params });
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load equipment models');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getManufacturers = createAsyncThunk(
  'lookup/getManufacturers',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/manufacturers');
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load manufacturers');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const getBankruptTypes = createAsyncThunk(
  'lookup/getBankruptTypes',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LOOKUP.BANKRUPT_TYPES);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load bankrupt types');
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

const initialState = {
  countries: [],
  usCountryId: null,
  states: [],
  attributeTypes: [],
  equipmentTypes: null,
  equipmentModels: null,
  manufacturers: null,
  bankruptTypes: null,
  loading: false,
  error: null,
};

const lookupSlice = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    clearLookupError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload ?? [];
      })
      .addCase(getUnitedStatesId.fulfilled, (state, action) => {
        state.loading = false;
        state.usCountryId = action.payload;
      })
      .addCase(getStates.fulfilled, (state, action) => {
        state.loading = false;
        state.states = action.payload ?? [];
      })
      .addCase(getAttributeTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.attributeTypes = action.payload ?? [];
      })
      .addCase(getEquipmentTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.equipmentTypes = action.payload;
      })
      .addCase(getEquipmentModels.fulfilled, (state, action) => {
        state.loading = false;
        state.equipmentModels = action.payload;
      })
      .addCase(getManufacturers.fulfilled, (state, action) => {
        state.loading = false;
        state.manufacturers = action.payload;
      })
      .addCase(getBankruptTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.bankruptTypes = action.payload;
      })
      .addMatcher(
        (a) =>
          [
            getCountries.pending,
            getUnitedStatesId.pending,
            getStates.pending,
            getAttributeTypes.pending,
            getEquipmentTypes.pending,
            getEquipmentModels.pending,
            getManufacturers.pending,
            getBankruptTypes.pending,
          ].some((t) => t.match(a)),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          [
            getCountries.rejected,
            getUnitedStatesId.rejected,
            getStates.rejected,
            getAttributeTypes.rejected,
            getEquipmentTypes.rejected,
            getEquipmentModels.rejected,
            getManufacturers.rejected,
            getBankruptTypes.rejected,
          ].some((t) => t.match(a)),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Something went wrong';
        }
      );
  },
});

export const { clearLookupError } = lookupSlice.actions;
export default lookupSlice.reducer;
